import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import RightPanel from '../../app/layout/RightPanel';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import EmptyState from '../../components/EmptyState';
import { MenuItem } from '../../components/Menu';
import InvitationModal from '../../invitation/InvitationModal';
import { inputToOptions } from '../../invitation/InvitationUserSelect';
import useInvitationEmailOption from '../../invitation/useInvitationEmailOption';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import { topicToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import { useAllUsersMap } from '../../user/useAllUsers';
import populateObjects from '../../utils/populateObjects';
import useSlug from '../../utils/router/useSlug';
import getFirstName from '../../utils/text/getFirstName';
import { TopicEntityIcon } from '../TopicIcon';
import topicPolicyCheck, {
  TopicPolicyAction,
  isOwner,
} from '../topicPolicyCheck';
import { useAllTopicMap } from '../useAllTopics';
import {
  useAddTopicGroup,
  useAddTopicUser,
  useRemoveTopicGroup,
  useRemoveTopicUser,
  useUpdateInheritParent,
  useUpdateTopicGroup,
  useUpdateTopicUser,
} from '../useTopicUserMutations';
import getTopicUsers, {
  getInheritedUsers,
  getTopicUserIdSet,
} from '../utils/getTopicUsers';
import TopicGroupRow from './TopicGroupRow';
import TopicUserCollapsibleList from './TopicUserCollapsibleList';
import TopicUserList from './TopicUserList';
import * as styles from './TopicUsersPanel.module.scss';
import getTopicAccessWarnings from './getTopicAccessWarning';

const joinNames = (obj: { name: string }[]) => {
  const names = obj.map(({ name }) => name);
  const { length } = names;
  return length <= 1
    ? names.join('')
    : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${names.slice(0, -1).join(', ')}, and ${names[length - 1]}`;
};

const removeDirectOwner = <T extends { owners?: { id: string }[] }>(
  topic: T,
  { id }: { id: string },
) =>
  topic && {
    ...topic,
    owners: topic.owners?.filter((owner) => owner.id !== id),
  };

const TOPIC_JOIN_TIP =
  'Become a member to receive notifications for new posts and edits in this topic.';

const TopicUsersPanel = () => {
  const [topicId] = useSlug('topicSlug');
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const allUsersMap = useAllUsersMap();
  const allTopicsMap = useAllTopicMap();
  const [input, setInput] = useState('');
  const hasInput = !!input;

  const baseTopic = allTopicsMap[topicId];
  const parentId = baseTopic?.parent?.id;
  const fullParent = parentId ? allTopicsMap[parentId] : undefined;

  const topic = useMemo(
    () => baseTopic && { ...baseTopic, parent: fullParent },
    [baseTopic, fullParent],
  );

  const addTopicUser = useAddTopicUser();
  const removeTopicUser = useRemoveTopicUser();
  const updateTopicUser = useUpdateTopicUser();
  const addTopicGroup = useAddTopicGroup();
  const removeTopicGroup = useRemoveTopicGroup();
  const updateTopicGroup = useUpdateTopicGroup();
  const updateInheritParent = useUpdateInheritParent();

  const getInvitationEmailOption = useInvitationEmailOption();

  const config = useMemo<EntitySelectConfig>(() => {
    if (!topic) {
      return {};
    }

    const topicUserIdSet = getTopicUserIdSet(topic);

    const {
      membership: {
        ownerUserIds,
        memberUserIds,
        ownerGroupIds,
        memberGroupIds,
      },
      parent,
    } = topic;

    const userIdSet = hasInput
      ? // If input is not empty, omit only users which has direct access
        new Set([...(memberUserIds || []), ...(ownerUserIds || [])])
      : // If input is empty, omit users which have access through parent topic or groups
        new Set([...topicUserIdSet.members, ...topicUserIdSet.owners]);

    const inheritedGroupIdsSet = new Set(
      topic.inheritedAncestors.flatMap((ancestor) => [
        ...ancestor.ownerGroups.map((g) => g.id),
        ...ancestor.memberGroups.map((g) => g.id),
      ]),
    );

    const groupIdSet = new Set([
      // If input is empty, omit groups which have access through parent topic
      ...(hasInput ? [] : [...inheritedGroupIdsSet]),
      ...(memberGroupIds || []),
      ...(ownerGroupIds || []),
    ]);

    const isInheritable =
      !!parent &&
      !topic.inheritParent &&
      topicPolicyCheck(TopicPolicyAction.AddRemoveOwner, topic, currentUser);

    return {
      user: { preferredMaxCount: 5, filter: (user) => !userIdSet.has(user.id) },
      group: {
        preferredMaxCount: isInheritable ? 2 : 3,
        filter: (group) => !groupIdSet.has(group.id),
      },
      topic: {
        preferredMaxCount: isInheritable ? 1 : 0,
        label: 'Parent topic',
        filter: (t) => isInheritable && t.id === parent.id,
      },
      new: {
        showIfNoOthersMatch: true,
        getGroup: (inputValue) =>
          getInvitationEmailOption({
            inputValue,
            onSelect: (suggestedEmail) => {
              dispatch(
                UIActions.openModal(InvitationModal, {
                  initialOptions: suggestedEmail
                    ? (inputToOptions(suggestedEmail) ?? undefined)
                    : undefined,
                  initialValues: { memberIds: [topic.id] },
                }),
              );
            },
          }),
      },
      order: ['user', 'group', 'topic', 'new'],
    };
  }, [topic, hasInput, currentUser, getInvitationEmailOption, dispatch]);

  if (!topic) {
    return null;
  }

  const {
    memberGroups,
    members,
    name,
    ownerGroups,
    owners,
    parent,
    requestors,
  } = topic;
  const inheritedParent = topic.inheritParent ? parent : undefined;

  const flattenUsers = getTopicUsers(topic);
  const totalUserCount =
    flattenUsers.owners.length + flattenUsers.members.length;
  const hasCurrentUserJoined =
    _.some(flattenUsers.owners, { id: currentUser.id }) ||
    _.some(flattenUsers.members, { id: currentUser.id });

  const { owners: inheritedOwners, members: inheritedMembers } =
    getInheritedUsers(topic);

  const canEditOwners =
    hasCurrentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveOwner, topic, currentUser);

  const canEditMembers =
    hasCurrentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveMember, topic, currentUser);

  const canCurrentUserJoin =
    !hasCurrentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveMember, topic, currentUser);

  const handleRemoveGroup = async (group: { id: string; name: string }) => {
    await removeTopicGroup(topic, group);
    dispatch(
      UIActions.pushToast(
        topicToasts.memberRemovedWarning({
          groupName: group.name,
          topicName: topic.name,
        }),
      ),
    );
  };

  const populateGroupUsers = <T extends { allUsers: { id: string }[] }>(
    group: T,
  ) => ({ ...group, allUsers: populateObjects(allUsersMap, group.allUsers) });

  return (
    <RightPanel.Inner
      header={
        <RightPanel.Header
          title={
            totalUserCount > 0
              ? `${totalUserCount} ${inflection.inflect('member', totalUserCount)}`
              : ''
          }
        />
      }
      footer={
        totalUserCount > 0 && (canEditMembers || canCurrentUserJoin) ? (
          <div className={styles.footerContainer}>
            {canEditMembers && (
              <div className={styles.footer}>
                <h4>Add other members</h4>
                <div className={styles.helpText}>
                  Members will receive notifications for new posts, and can
                  manage other members in this topic.
                </div>
                <CategorizedEntitySelect
                  placeholder={`Add member to ${topic.name}`}
                  aria-label={`Add member to ${topic.name}`}
                  maxOptionsToShow={8}
                  config={config}
                  value={null}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    if (value.type === 'user') {
                      addTopicUser(topic, value.user);
                    } else if (value.type === 'group') {
                      addTopicGroup(topic, value.group);
                    } else if (value.type === 'topic') {
                      updateInheritParent(topic, true);
                    }
                  }}
                  inputValue={input}
                  onInputChange={setInput}
                />
              </div>
            )}
            {canCurrentUserJoin && (
              <div className={styles.footer}>
                <h4>Join topic</h4>
                <div className={styles.helpText}>{TOPIC_JOIN_TIP}</div>
                <Button
                  block
                  variant="primary"
                  onClick={() => addTopicUser(topic, currentUser)}
                >
                  Join {name}
                </Button>
              </div>
            )}
          </div>
        ) : undefined
      }
    >
      <div className={styles.container}>
        {totalUserCount === 0 && (
          <EmptyState
            imageName="genericAlt"
            title="No members yet"
            message={TOPIC_JOIN_TIP}
            action={
              <Button
                onClick={() => addTopicUser(topic, currentUser)}
                variant="primary"
              >
                Join {name}
              </Button>
            }
            vertical
          />
        )}
        {totalUserCount > 0 && (
          <div className={styles.list}>
            {inheritedParent && (
              <TopicUserCollapsibleList
                icon={<TopicEntityIcon />}
                members={populateObjects(allUsersMap, inheritedMembers)}
                name={inheritedParent.name}
                owners={populateObjects(allUsersMap, inheritedOwners)}
                menu={
                  canEditOwners && (
                    <MenuItem
                      danger
                      onClick={async () => {
                        await updateInheritParent(topic, false);
                        dispatch(
                          UIActions.pushToast(
                            topicToasts.noLongerInherited({
                              inheritedParentName: inheritedParent.name,
                              topicName: topic.name,
                              onUndo: () => {
                                updateInheritParent(topic, true);
                              },
                            }),
                            { duration: 6000 },
                          ),
                        );
                      }}
                    >
                      Remove from topic
                    </MenuItem>
                  )
                }
              />
            )}
            {ownerGroups.map((group) => (
              <TopicGroupRow
                key={group.id}
                group={populateGroupUsers(group)}
                owner
                {...(canEditOwners
                  ? {
                      onToggleOwnership: () =>
                        updateTopicGroup(topic, group, 'owner', 'member'),
                      onRemove: () => handleRemoveGroup(group),
                    }
                  : {})}
              />
            ))}
            {memberGroups.map((group) => (
              <TopicGroupRow
                key={group.id}
                group={populateGroupUsers(group)}
                onToggleOwnership={
                  canEditOwners
                    ? () => updateTopicGroup(topic, group, 'member', 'owner')
                    : undefined
                }
                onRemove={
                  canEditMembers ? () => handleRemoveGroup(group) : undefined
                }
              />
            ))}
            <TopicUserList
              topic={topic}
              members={populateObjects(allUsersMap, members)}
              owners={populateObjects(allUsersMap, owners)}
              requestors={populateObjects(allUsersMap, requestors)}
              removeUser={async (user) => {
                const userPreviousRole = isOwner(topic, user)
                  ? 'owner'
                  : 'member';

                await removeTopicUser(topic, user);
                const fullUser = allUsersMap[user.id];

                if (!fullUser) {
                  return;
                }

                const firstName = getFirstName(fullUser.name) ?? '';
                const access = getTopicAccessWarnings(topic, fullUser);

                if (access?.groups?.length) {
                  dispatch(
                    UIActions.pushToast(
                      topicToasts.noLongerDirectMemberButGroupWarning({
                        firstName,
                        topicName: topic.name,
                        accessLevel: access.level,
                        groupNames: joinNames(access.groups),
                        previousRole: userPreviousRole,
                      }),
                      { duration: 7000 },
                    ),
                  );
                } else if (access?.parent) {
                  dispatch(
                    UIActions.pushToast(
                      topicToasts.noLongerDirectMemberButParentTopicWarning({
                        firstName,
                        topicName: topic.name,
                        accessLevel: access.level,
                        parentTopicName: access.parent.name,
                        previousRole: userPreviousRole,
                      }),
                      { duration: 7000 },
                    ),
                  );
                } else {
                  dispatch(
                    UIActions.pushToast(
                      topicToasts.noLongerMember({
                        firstName,
                        topicName: topic.name,
                        previousRole: userPreviousRole,
                      }),
                      { duration: 7000 },
                    ),
                  );
                }
              }}
              updateUser={(user, oldRole, newRole) => {
                const doUpdate = async () => {
                  await updateTopicUser(topic, user, oldRole, newRole);
                  const fullUser = allUsersMap[user.id];

                  if (!fullUser) {
                    return;
                  }
                  if (oldRole === 'owner' && newRole === 'member') {
                    const firstName = getFirstName(fullUser.name) ?? '';
                    const { level: role, groups } =
                      getTopicAccessWarnings(topic, fullUser) || {};

                    if (role === 'owner') {
                      if (groups) {
                        dispatch(
                          UIActions.pushToast(
                            topicToasts.stillOwnerThroughGroupsWarning({
                              firstName,
                              groupNames: joinNames(groups),
                            }),
                            { duration: 4000 },
                          ),
                        );
                      } else if (inheritedParent) {
                        dispatch(
                          UIActions.pushToast(
                            topicToasts.stillOwnerThroughParentWarning({
                              firstName,
                              inheritedParentName: inheritedParent.name,
                            }),
                            { duration: 4000 },
                          ),
                        );
                      }
                    }
                  }
                };

                const shouldShowSelfDemotionConfirmation =
                  user.id === currentUser.id &&
                  currentUser.type !== 'ADMIN' &&
                  oldRole === 'owner' &&
                  newRole === 'member' &&
                  !isOwner(removeDirectOwner(topic, user), user);

                if (shouldShowSelfDemotionConfirmation) {
                  dispatch(
                    UIActions.openModal(ConfirmationModal, {
                      title: 'Demote yourself?',
                      description:
                        'You will not be able to promote yourself back to an owner.',
                      confirmButton: {
                        text: 'Demote',
                        onClick: doUpdate,
                      },
                    }),
                  );
                } else {
                  doUpdate();
                }
              }}
            />
            {parent == null && currentUser.type === 'GUEST' && (
              <div className={styles.guestWarning}>
                As a guest, you may not be seeing all members of this topic.
              </div>
            )}
          </div>
        )}
      </div>
    </RightPanel.Inner>
  );
};

export default TopicUsersPanel;
